import {
  BrandsAndProductsResponse,
  CompanyCompetitorsWithThemesResponse,
  CompanyCountryExposureResponse,
  CompanyRegionExposureResponse,
  PostSuggestedThemesResponse,
  SnakeMetaResponseV2,
  ThemeChildrenResponse,
  ThemeCompaniesResponse,
  ThemeCompetitorResponse,
  ThemeExposureResponse,
  ThemeSchemaResponse,
  ThemeSchemaResponseV3,
  TimeSeriesData,
  themeOverview,
} from '@api';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { apiFetch, wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

export enum EntityAnalyticsReportType {
  VALUATION = 'valuation',
  ENTRY_POINT = 'entry_point',
  POSITIONING = 'positioning',
  MOMENTUM = 'momentum',
  EQUITY_YIELD = 'equity_yield',
  EQUITY_GROWTH_GAUGE = 'equity_growth_gauge',
  EQUITY_CONSENSUS_TREND = 'equity_consensus_trend',
  VOLUME_ANALYSIS = 'volume_analysis',
  ECONOMIC_GROWTH_GAUGE = 'economic_growth_gauge',
  ECONOMIC_INFLATION_GAUGE = 'economic_inflation_gauge',
  FINANCIAL_CONDITIONS = 'financial_conditions',
  VOLATILITY = 'volatility',
  MAIN_MARKET_PRICE = 'main_market_price',
  MAIN_YIELD = 'main_yield',
  TECHNICAL_ANALYSIS = 'technical_analysis',
  GEOPOLITICAL = 'geopolitical',
  SEASONALITY = 'seasonality',
  SENTIMENT = 'sentiment',
}

interface SummaryReportItemDrillDown {
  label: string;
  latest_value: number;
  percentile: number;
  snake: string;
  method: string;
}

interface SummaryReportItem {
  label: string;
  method_sub_cls: EntityAnalyticsReportType;
  percentile: number;
  pill: string;
  drill_down: SummaryReportItemDrillDown[];
  asset_cls: string;
}

export const aoUrls = {
  OW_ASSET_ITEMS_V3: `co/v3/w/f`,
  OW_SNAKE_BY_ENTITY: `ca/v2/w/p`,
  OW_SNAKE_BY_NAME: `ca/v3/q`,
  SNAKE_META_V3: `snake/v3`,
  CONNECTED_ENTITIES: `kg/v1/connected-entities/proximity`,
};

export const fetchAnalyseData = (entityName: string) =>
  apiFetch<SummaryReportItem[]>(aoUrls.OW_ASSET_ITEMS_V3, {
    method: 'post',
    body: { entity: entityName },
  });

export const fetchSnakeByEntity = (entityName: string, nDays = 0) => {
  const params = nDays !== undefined ? { n_days: nDays } : {};
  return apiFetch<TimeSeriesData>(aoUrls.OW_SNAKE_BY_ENTITY, {
    method: 'post',
    body: { entity: entityName, ...params },
  });
};

export const fetchSnakeByName = (
  snakeName: string,
  nDays?: number,
  fromDate?: string,
  signal?: AbortSignal
) => {
  const params: Record<string, string | number> = {};

  if (fromDate) {
    params['from_date'] = fromDate;
  }

  if (nDays) {
    params['date_since'] = nDays;
  }

  return apiFetch<TimeSeriesData>(aoUrls.OW_SNAKE_BY_NAME, {
    method: 'post',
    body: { snake_expression: snakeName, ...params, static: 1 },
    options: { signal },
  });
};

export const fetchSnakeMeta = (snake: string, signal?: AbortSignal) =>
  wretchRequest<SnakeMetaResponseV2>(`${aoUrls.SNAKE_META_V3}?q=${snake}`, {
    options: { signal },
  }).then(response => {
    // The snake passed from prop it could be aapl.price, but the returned object key can be aapl.price, so it won't match
    const key = Object.keys(response)[0];
    return response[key];
  });

export const fetchConnectedEntities = (entity: string) =>
  apiFetch<string[]>(aoUrls.CONNECTED_ENTITIES, {
    method: 'post',
    body: { entity },
  });

export const fetchCompanyTheme = ({
  entityTag,
  limit,
  signal,
}: {
  entityTag: string;
  limit?: number;
  signal?: AbortSignal;
}) =>
  wretchRequest<ThemeExposureResponse>(
    themeOverview.getCompanyTheme.path(entityTag, limit),
    {
      schema: themeOverview.getCompanyTheme.schema,
      signal,
    }
  );

export type FetchThemeResponse = ThemeSchemaResponse | ThemeSchemaResponseV3;

export const fetchTheme = <T extends FetchThemeResponse = ThemeSchemaResponse>({
  themeId,
  signal,
}: {
  themeId: string;
  signal?: AbortSignal;
}) => {
  const isFeatureFlagEnabled = useFlags
    .getState()
    .getFeatureFlagState('theme_v3');
  if (isFeatureFlagEnabled) {
    return wretchRequest<ThemeSchemaResponseV3>(
      themeOverview.getThemeV3.path(themeId),
      {
        schema: themeOverview.getThemeV3.schema,
        signal,
      }
    ) as Promise<T>;
  }

  return wretchRequest<ThemeSchemaResponse>(
    themeOverview.getThemeV2.path(themeId),
    {
      schema: themeOverview.getThemeV2.schema,
      signal,
    }
  ) as Promise<T>;
};

export const fetchCompanyThemeCompetitors = ({
  entityTag,
  themeId,
  limit,
  signal,
}: {
  entityTag: string;
  themeId: string;
  limit?: number;
  signal?: AbortSignal;
}) =>
  wretchRequest<ThemeCompetitorResponse>(
    themeOverview.getCompanyCompetitors.path(entityTag, themeId, limit),
    {
      schema: themeOverview.getCompanyCompetitors.schema,
      signal,
    }
  );

export const fetchCompanyCompetitorsWithThemes = ({
  entityTag,
  limit,
  signal,
}: {
  entityTag: string;
  limit?: number;
  signal?: AbortSignal;
}) =>
  wretchRequest<CompanyCompetitorsWithThemesResponse>(
    themeOverview.getCompanyCompetitorsWithThemes.path(entityTag, limit),
    {
      schema: themeOverview.getCompanyCompetitorsWithThemes.schema,
      signal,
    }
  );

export const fetchCompanyCountryExposure = ({
  entityTag,
  signal,
}: {
  entityTag: string;
  signal?: AbortSignal;
}) =>
  wretchRequest<CompanyCountryExposureResponse>(
    themeOverview.getCompanyCountryExposure.path(entityTag),
    {
      schema: themeOverview.getCompanyCountryExposure.schema,
      signal,
    }
  );

export const fetchCompanyRegionExposure = ({
  entityTag,
  signal,
}: {
  entityTag: string;
  signal?: AbortSignal;
}) =>
  wretchRequest<CompanyRegionExposureResponse>(
    themeOverview.getCompanyRegionExposure.path(entityTag),
    {
      schema: themeOverview.getCompanyRegionExposure.schema,
      signal,
    }
  );

export const fetchThemeCompanies = ({
  themeId,
  limit,
  signal,
}: {
  themeId: string;
  limit?: number;
  signal?: AbortSignal;
}) =>
  wretchRequest<ThemeCompaniesResponse>(
    themeOverview.getThemeCompanies.path(themeId, limit),
    {
      schema: themeOverview.getThemeCompanies.schema,
      signal,
    }
  );

export const getThemeCompanies = ({
  themeId,
  limit,
  signal,
}: {
  themeId: string;
  limit?: number;
  signal?: AbortSignal;
}) =>
  apiFetch<ThemeCompaniesResponse>(
    themeOverview.getThemeCompanies.path(themeId, limit),
    {
      schema: themeOverview.getThemeCompanies.schema,
      signal,
    }
  );

export const fetchThemeChildren = (
  themeId: string,
  entityTag?: string,
  signal?: AbortSignal
) =>
  wretchRequest<ThemeChildrenResponse>(
    themeOverview.getThemeChildren.path(themeId),
    {
      schema: themeOverview.getThemeChildren.schema,
      signal,
      method: 'post',
      body: {
        ...(!!entityTag ? { company: entityTag } : {}),
      },
    }
  );

export const fetchSuggestedThemes = (
  entityTags: string[],
  signal?: AbortSignal
) =>
  wretchRequest<PostSuggestedThemesResponse>(themeOverview.postSuggested.path, {
    method: 'post',
    schema: themeOverview.postSuggested.schema,
    body: { entity_tags: entityTags },
    signal,
  });

export const getBrandsAndProducts = ({
  entityTag,
  signal,
}: {
  entityTag: string;
  signal?: AbortSignal;
}) =>
  wretchRequest<BrandsAndProductsResponse>(
    themeOverview.getBrandsAndProducts.path(entityTag),
    {
      schema: themeOverview.getBrandsAndProducts.schema,
      signal,
    }
  );
